import React, { useState, useEffect, ReactElement } from "react";
import { useDispatch } from 'react-redux'
import type { Dispatch } from 'redux'
import { actions as projectLocationActions } from '../../../actions/projectLocationActions';
import * as types from '../../../constants/actionTypes';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import Drawer61 from '../../Common/Drawer/Drawer61';
import Notification from '../../Common/Notification';
import MultiSelectBox from '../../Common/MultiSelectBox/MultiSelectBox';
import TextArea from '../../Common/TextArea';
import { Option, LocationTagOption, ProjectLocationSearchParams } from '../../../types/common';

interface FilterLocationsDrawerProps {
    subscriptionId: number;
    onCancel: any;
    onFilter: any;
    searchParams: ProjectLocationSearchParams;
    setSearchParams: React.Dispatch<React.SetStateAction<ProjectLocationSearchParams>>;
}

interface LocationFilterResponse {
    areas: Array<Option>;
    brands: Array<Option>;
    continents: Array<Option>;
    countries: Array<Option>;
    locationTypes: Array<Option>;
    managementCompanies: Array<Option>;
    regions: Array<Option>;
    tags: Array<LocationTagOption>;
}

const FilterLocationsDrawer = ({ subscriptionId, onCancel, onFilter, searchParams, setSearchParams }: FilterLocationsDrawerProps): ReactElement => {

    const dispatch: Dispatch = useDispatch();
    const getFilters = async (subId) => dispatch(projectLocationActions.loadLocationFilters(subId));

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [filters, setFilters] = useState<LocationFilterResponse>({
        areas: [],
        brands: [],
        continents: [],
        countries: [],
        managementCompanies: [],
        locationTypes: [],
        regions: [],
        tags: []
    });
    const [abbreviations, setAbbreviations] = useState<string>(searchParams.abbreviations.join(','));

    useEffect(() => {
        loadFilters();
    }, []);

    const loadFilters = async () => {

        setIsLoading(true);

        // Load the various lookup lists for brands, areas, countries, continents, types, management cos, regions and tags
        try {
            const result = await getFilters(subscriptionId);
            const payload = result.payload;
            if (result.type === types.LOAD_PROJECTLOCATION_LOCATION_FILTERS_SUCCESS) {
                setFilters(payload);
            } else if (result.type === types.LOAD_PROJECTLOCATION_LOCATION_FILTERS_FAILURE) {
                Notification("error", "An Error Occurred", payload.message)
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            setIsLoading(false);
        }
    };

    const onSelectChange = (csv, name) => {
        if (csv === "")
            setSearchParams(prevState => ({ ...prevState, [name]: [] }));
        else
            setSearchParams(prevState => ({ ...prevState, [name]: csv.split(",") }));
    };

    const onAbbreviationChange = (e) => {
        //const { name, value } = e.target;
        //setSearchParams(prevState => ({ ...prevState, [name]: value }));
        setAbbreviations(e.target.value);
    };

    const onApplyClick = () => {
        const filterParams = { ...searchParams, abbreviations: searchTermsToArray(abbreviations) };
        onFilter(filterParams);
    };

    // we want the user to have the option of searching by a comma-delimited, space-delimited, or newline delimited list
    const searchTermsToArray = (terms) => {

        if (!terms)
            return [];

        if (terms.includes(','))
            return terms.split(',')

        // if they copy/pasted from excel it will carry over newlines that need to be replaced with spaces
        terms = terms.replace(/\n/g, ' ');
        return terms.split(' ');
    }

    return (
        <div className="filter-locations-drawer">
            <Drawer61 isWide={false}
                title='Filter Available Locations'
                onCancelClick={onCancel}
                onSaveClick={onApplyClick}
                saveText='Apply'
            >
                <LoadingIndicator isLoading={isLoading}>

                    <div className="row">
                        <div className="col-12">
                            <MultiSelectBox
                                name="brandIds"
                                label="Brands"
                                idField="id"
                                valueField="name"
                                options={filters.brands}
                                selectedValues={searchParams.brandIds.toString()}
                                onChange={onSelectChange} isMulti={true} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <MultiSelectBox
                                name="managementCompanyIds"
                                label="Management Companies"
                                idField="id"
                                valueField="name"
                                options={filters.managementCompanies}
                                selectedValues={searchParams.managementCompanyIds.toString()}
                                onChange={onSelectChange}
                                isMulti={true} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <MultiSelectBox
                                name="areaIds"
                                label="Areas"
                                idField="id"
                                valueField="name"
                                options={filters.areas}
                                selectedValues={searchParams.areaIds.toString()}
                                onChange={onSelectChange}
                                isMulti={true} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <MultiSelectBox
                                name="countryIds"
                                label="Countries"
                                idField="id"
                                valueField="name"
                                options={filters.countries}
                                selectedValues={searchParams.countryIds.toString()}
                                onChange={onSelectChange}
                                isMulti={true} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <MultiSelectBox
                                name="continentIds"
                                label="Continents"
                                idField="id"
                                valueField="name"
                                options={filters.continents}
                                selectedValues={searchParams.continentIds.toString()}
                                onChange={onSelectChange}
                                isMulti={true} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <MultiSelectBox
                                name="typeIds"
                                label="Types"
                                idField="id"
                                valueField="name"
                                options={filters.locationTypes}
                                selectedValues={searchParams.typeIds.toString()}
                                onChange={onSelectChange}
                                isMulti={true} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <MultiSelectBox
                                name="regionIds"
                                label="Regions"
                                idField="id"
                                valueField="name"
                                options={filters.regions}
                                selectedValues={searchParams.regionIds.toString()}
                                onChange={onSelectChange}
                                isMulti={true} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <MultiSelectBox
                                name="tagIds"
                                options={filters.tags}
                                idField="id"
                                valueField="name"
                                selectedValues={searchParams.tagIds.toString()}
                                onChange={onSelectChange}
                                isMulti={true}
                                label="Tags"
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <TextArea rows={5} maxLength={3000} name="abbreviations" label="Location Abbreviations" type="text" placeholder="ABC, XYZ" value={abbreviations} onChange={onAbbreviationChange} />
                        </div>
                        <div className="col-12 text-end">
                            <div className="input-subtitle">Separate multiple values with comma</div>
                        </div>
                    </div>

                </LoadingIndicator>

            </Drawer61>
        </div>
    );
}

export default FilterLocationsDrawer;
